/**
 * @file machine_app_state.js
 * @author MachineApps Team
 *
 * @description Contains the globally loaded state of MachineApps. Note that the data stored here should only
 * ever exist ONCE.
 *
 * NOTE: This file is intended to be platform agnostic by design. If you would like some loading functionality
 * or something, please send it to functions as a callback. For example, we never care where we get our data
 * from, we just call a function sent as a parameter.
 */


const machine_app_implementation_manager = require('./machine_app_implementation_manager');
const machine_app_library = require('./machine_app_library');
const machine_app_implementation_loader = require('./machine_app_implementation_loader');
const { machineAppInterface } = require('./machine_app_interface_singleton');
const machine_app_dev_reload_button = require('../machine_app_dev_reload_button');
let gIsInitialized = false;
const gOnInitializeQueue = [];


/**
 * Retrieve the global definitions list
 */
function getImplementationManager() {
  return machine_app_implementation_manager.instance;
}

/**
 * Retrieve the library of saved MachineApp instances.
 */
function getLibraryManager() {
  return machine_app_library.instance;
}

function getInterface() {
  return machineAppInterface.get();
}

/**
 * Add a callback that will be executed once the MachineApps Library has been loaded from a remote source.
 * This is helpful when you want to ensure that you have the Library before doing some action.
 *
 * @param {Function} pCallback
 */
function waitForInitialized(pCallback) {
  if (gIsInitialized)  {
    pCallback();
  }
  else {
    gOnInitializeQueue.push(pCallback);
  }
}

/**
 * Initialize the definitions list and the library.
 *
 * @param {Object} pMachineAppComponents Module from which we will receive machine app components definitions.
 * @param {Library} pMachineLogicLibrary
 * @param {Function<Function> => void} pGetLibrary Function called to retrieve the library from the data store.
 * @param {Object} pSaveFunctionality Object that defines how we can save a MachineApp.
 * @param {() => BaseMachineAppInterface} Function to instantiate a MachineAppInterface instance.
 * @param {Function<Boolean>} pCallback Function called when loading is complete.
 */
function initialize(pMachineAppComponents, pMachineLogicLibrary, pGetLibrary, pSaveFunctionality, pInitializeInterface, pCallback) {
  if (machine_app_implementation_manager.instance.isLoaded() && machine_app_library.instance.isLoaded()) {
    pCallback(true); return;
  }

  // Note: Order is important here. The MachineAppLibrary relies on the interface being initialized.
  machineAppInterface.set(pInitializeInterface());
  machine_app_library.instance.setMachineLogicLibrary(pMachineLogicLibrary);
  machine_app_library.instance.setSaveFunctionality(pSaveFunctionality);

  // TODO (machineapps) Load the definitions from a remote source
  machine_app_implementation_manager.instance.load();
  machine_app_implementation_loader.instance.load(machine_app_implementation_manager.instance, pMachineAppComponents, function() {
    pGetLibrary(function(pLibrary) {
      machine_app_library.instance.fromJson(pLibrary);
      machine_app_dev_reload_button.refresh();

      // After loading MachineApps, let's clean up any dangling MachineLogic applications that we find.
      gIsInitialized = true;
      pCallback(true);

      gOnInitializeQueue.forEach(function(pCb) { pCb(); });
    });
  });

}

module.exports = {
  initialize               : initialize,
  waitForInitialized       : waitForInitialized,
  getImplementationManager : getImplementationManager,
  getLibraryManager        : getLibraryManager,
  getInterface             : getInterface
};